import React, { useEffect, useRef } from 'react';
import './PageDisplay.css';

const PageDisplay = ({ pageResults, pageNumber, selectedTranslations, onFilterChange, onPageNavigation, highlightedLine }) => {
  const highlightedLineRef = useRef(null);

  // Scroll to highlighted line when it appears
  useEffect(() => {
    if (highlightedLine && highlightedLineRef.current) {
      setTimeout(() => {
        highlightedLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlightedLine]);
  // Create color palette for researchers
  const colors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6610f2',
    '#d63384', '#0d6efd', '#198754', '#ff0000', '#00ff00',
    '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#800000'
  ];

  // Create researcher to color mapping (sorted alphabetically for consistency)
  const allResearchers = [...new Set(pageResults.flatMap(v => (v.translations || []).map(t => t.researcher_name)))].sort();
  const researcherToColor = {};
  allResearchers.forEach((name, idx) => {
    researcherToColor[name] = colors[idx % colors.length];
  });

  // Group lines by verse_number to keep translations together
  const linesByVerse = {};
  pageResults.forEach(line => {
    if (!linesByVerse[line.verse_number]) {
      linesByVerse[line.verse_number] = [];
    }
    linesByVerse[line.verse_number].push(line);
  });

  return (
    <div className="page-display" style={{padding: '1px'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '100%', position: 'relative' }}>
        <button
          onClick={() => {
            const prevPage = parseInt(pageNumber) - 1;
            if (prevPage > 0 && onPageNavigation) {
              onPageNavigation(prevPage);
            }
          }}
          disabled={!onPageNavigation || parseInt(pageNumber) <= 1}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (!onPageNavigation || parseInt(pageNumber) <= 1) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: (!onPageNavigation || parseInt(pageNumber) <= 1) ? 0.5 : 1,
            transition: 'background-color 0.3s ease'
          }}
          onMouseOver={(e) => {
            if (onPageNavigation && parseInt(pageNumber) > 1) {
              e.target.style.backgroundColor = '#0056b3';
            }
          }}
          onMouseOut={(e) => {
            if (onPageNavigation && parseInt(pageNumber) > 1) {
              e.target.style.backgroundColor = '#007bff';
            }
          }}
          title="Previous Page"
        >
          Previous Page
        </button>
        <button
          onClick={() => {
            if (onPageNavigation) {
              const nextPage = parseInt(pageNumber) + 1;
              onPageNavigation(nextPage);
            }
          }}
          disabled={!onPageNavigation}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: !onPageNavigation ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.3s ease'
          }}
          onMouseOver={(e) => {
            if (onPageNavigation) {
              e.target.style.backgroundColor = '#0056b3';
            }
          }}
          onMouseOut={(e) => {
            if (onPageNavigation) {
              e.target.style.backgroundColor = '#007bff';
            }
          }}
          title="Next Page"
        >
          Next Page
        </button>
        <h2 style={{ margin: 0, textAlign: 'center', position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: '200px' }}>Page {pageNumber}</h2>
        <div className="mobile-nav-buttons">
          <button
            onClick={() => {
              const prevPage = parseInt(pageNumber) - 1;
              if (prevPage > 0 && onPageNavigation) {
                onPageNavigation(prevPage);
              }
            }}
            disabled={!onPageNavigation || parseInt(pageNumber) <= 1}
            style={{
              padding: '6px 12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (!onPageNavigation || parseInt(pageNumber) <= 1) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              opacity: (!onPageNavigation || parseInt(pageNumber) <= 1) ? 0.5 : 1,
              transition: 'background-color 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (onPageNavigation && parseInt(pageNumber) > 1) {
                e.target.style.backgroundColor = '#0056b3';
              }
            }}
            onMouseOut={(e) => {
              if (onPageNavigation && parseInt(pageNumber) > 1) {
                e.target.style.backgroundColor = '#007bff';
              }
            }}
            title="Previous Page"
          >
            Previous Page
          </button>
          <button
            onClick={() => {
              if (onPageNavigation) {
                const nextPage = parseInt(pageNumber) + 1;
                onPageNavigation(nextPage);
              }
            }}
            disabled={!onPageNavigation}
            style={{
              padding: '6px 12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !onPageNavigation ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'background-color 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (onPageNavigation) {
                e.target.style.backgroundColor = '#0056b3';
              }
            }}
            onMouseOut={(e) => {
              if (onPageNavigation) {
                e.target.style.backgroundColor = '#007bff';
              }
            }}
            title="Next Page"
          >
            Next Page
          </button>
        </div>
      </div>

      <div>
        {Object.keys(linesByVerse).sort((a, b) => a - b).map(verseNum => {
          const verseLines = linesByVerse[verseNum];
          
          return (
            <div key={verseNum} style={{ marginBottom: '8px' }}>
              <div>
                {verseLines.map((line, idx) => {
                  const isHighlighted = highlightedLine && 
                    highlightedLine.page_number === line.page_number &&
                    highlightedLine.verse_number === line.verse_number &&
                    highlightedLine.line_number === line.line_number;
                  
                  return (
                    <p 
                      key={idx} 
                      id={isHighlighted ? `highlighted-line-${line.line_number}` : undefined}
                      className="phrase-line"
                      style={{ 
                        fontSize: '18px',
                        cursor: 'default',
                        position: 'relative',
                        textAlign: 'left',
                        color: isHighlighted ? 'orange' : '#003d7a',
                        fontWeight: isHighlighted ? 'bold' : 'normal',
                        scrollMarginTop: isHighlighted ? '100px' : '0'
                      }}
                      title={`Page ${line.page_number} • Verse ${line.verse_number} • Line ${line.line_number}`}
                      ref={isHighlighted ? highlightedLineRef : null}
                    >
                      {line.line_text}
                    </p>
                  );
                })}
              </div>

              {selectedTranslations.length > 0 && (
                <div style={{ marginTop: '0px', marginBottom: '25px', paddingLeft: '20px', borderLeft: '3px solid #ccc' }}>
                  {verseLines.map((line, lineIdx) => {
                    const filteredTranslations = line.translations && line.translations.filter(t => selectedTranslations.includes(t.researcher_name));
                    const isLastLine = lineIdx === verseLines.length - 1;
                    return (
                      <div key={lineIdx} style={{ marginBottom: '8px' }}>
                        {filteredTranslations && filteredTranslations.map((translation, transIdx) => {
                          const isLastInLine = transIdx === filteredTranslations.length - 1;
                          const isLastTranslation = isLastLine && isLastInLine;
                          return (
                            <div key={transIdx} style={{ marginBottom: isLastTranslation ? '0' : '4px', paddingLeft: '1px', textAlign: 'left', fontSize: '14px' }}>
                              <strong>
                                <span style={{ 
                                  display: 'inline-block', 
                                  width: '12px', 
                                  height: '12px', 
                                  borderRadius: '50%', 
                                  backgroundColor: researcherToColor[translation.researcher_name],
                                  marginRight: '6px',
                                  verticalAlign: 'middle'
                                }}></span>
                                {translation.researcher_name}
                              </strong>: {translation.translation_text}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {onPageNavigation && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '10px', paddingBottom: '20px', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <button
              onClick={() => {
                const prevPage = parseInt(pageNumber) - 1;
                if (prevPage > 0) {
                  onPageNavigation(prevPage);
                }
              }}
              disabled={parseInt(pageNumber) <= 1}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: parseInt(pageNumber) <= 1 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: parseInt(pageNumber) <= 1 ? 0.5 : 1,
                transition: 'background-color 0.3s ease',
                minWidth: '120px'
              }}
              onMouseOver={(e) => {
                if (parseInt(pageNumber) > 1) {
                  e.target.style.backgroundColor = '#0056b3';
                }
              }}
              onMouseOut={(e) => {
                if (parseInt(pageNumber) > 1) {
                  e.target.style.backgroundColor = '#007bff';
                }
              }}
            >
              Previous Page
            </button>
            {parseInt(pageNumber) > 1 && (
              <span style={{ fontSize: '14px', color: '#666' }}>Page {parseInt(pageNumber) - 1}</span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#000000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background-color 0.3s ease',
                minWidth: '120px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#333333';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#000000';
              }}
            >
              Return to Top
            </button>
            <span style={{ fontSize: '14px', color: '#666' }}>Page {pageNumber}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <button
              onClick={() => {
                const nextPage = parseInt(pageNumber) + 1;
                onPageNavigation(nextPage);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background-color 0.3s ease',
                minWidth: '120px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#0056b3';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#007bff';
              }}
            >
              Next Page
            </button>
            <span style={{ fontSize: '14px', color: '#666' }}>Page {parseInt(pageNumber) + 1}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageDisplay;

